<template>
  <div class="timeTrackingTable">
    <DataTable
      class="p-datatable-sm"
      :value="timeTrackings"
      :loading="isLoading"
      :rowClass="rowClass"
      :rowHover="true"
      dataKey="number"
      :lazy="true"
      :filters.sync="tableState.filters"
      stateStorage="local"
      :stateKey="filterName"
      filterDisplay="row"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      :totalRecords="getTimeTrackingCount"
      :paginator="true"
      :rows.sync="tableState.pagination.rowsPerPage"
      :first="pageOffset"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="'Anzeigen ' + '{first} bis {last} von {totalRecords} Einträgen'"
      :rowsPerPageOptions="[10, 20, 50, 100, 200]"
    >
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="startTimeDay"
        :header="$t('day')"
        sortable
        :sortField="'startTime'"
        :headerStyle="{ width: '180px' }"
        :showFilterMenu="false"
        filterField="dateRange"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.startTime">
            {{ formatDateWithDots(slotProps.data.startTime) }}
          </span>
        </template>
      </Column>

      <Column
        field="startTime"
        :header="$t('start')"
        :styles="{ width: '100px' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <strong> {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong>
        </template>
      </Column>

      <Column
        field="stopTime"
        :header="$t('end')"
        :styles="{ width: '100px' }"
        sortable
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <strong v-if="slotProps.data[slotProps.column.field]">
            {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong
          >
          <strong v-else>
            <LoopingRhombusesSpinner
              :animation-duration="12000"
              :rhombus-size="9"
              color="#495057"
            />
          </strong>
        </template>
      </Column>

      <Column field="duration" :header="$t('duration')" sortable :headerStyle="{ width: '100px' }">
        <template #body="slotProps">
          <span>
            {{ formatDuration(slotProps.data.duration || 0) }}
          </span>
        </template>
      </Column>

      <Column field="user" :header="$t('employee')" sortable :showFilterMenu="false">
        <template #body="slotProps">
          <div>
            {{ slotProps.data.user.firstname }}
            {{ slotProps.data.user.lastname }}
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { FilterMatchMode } from 'primevue/api';
import { getTimeTrackingStateColor } from '@/helpers/colors';
import { LoopingRhombusesSpinner } from 'epic-spinners';

export default {
  name: 'TimeTrackingTable',
  components: {
    DataTable,
    Column,
    LoopingRhombusesSpinner,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      filteredUsers: [],
      displayEditDialog: false,
      editedTimeTracking: null,
      originalStartTime: null,
      isNewTimeTracking: false,
      filteredEmployees: [],

      defaultFilters: {
        projectNumber: {
          value: this.project ? this.project.number : null,
          matchMode: FilterMatchMode.EQUALS,
        },
        dateRange: {
          range: [
            this.$dayjs()
              .startOf('day')
              .toDate(),
            this.$dayjs()
              .endOf('day')
              .toDate(),
          ],
        },
      },
      defaultCustomFilters: {
        timeTrackingStates: [],
        timeTrackingCustomerSearch: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      },

      tableState: {
        filterName: 'project-timeTrackings-table-filters',
        customFilterName: 'project-timeTrackings-custom-table-filters',
        filters: {
          projectNumber: {
            value: this.project ? this.project.number : null,
            matchMode: FilterMatchMode.EQUALS,
          },
          dateRange: {
            range: [
              this.$dayjs()
                .startOf('day')
                .toDate(),
              this.$dayjs()
                .endOf('day')
                .toDate(),
            ],
          },
        },
        customFilters: this.defaultCustomFilters,
        pagination: {
          page: 0,
          rowsPerPage: 10,
        },
        sortField: 'startTime',
        sortOrder: -1,
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters([
      'getTimeTrackings',
      'getEmployees',
      'getEnumValues',
      'getTimeTrackingCount',
      'getTimeTrackingTotalHours',
      'isLoading',
      'getUsers',
    ]),
    isFormValid() {
      if (!this.editedTimeTracking) return false;
      return (
        this.editedTimeTracking.user &&
        this.editedTimeTracking.startTime &&
        this.editedTimeTracking.stopTime
      );
    },
    getTimeTrackingTotalHours() {
      return this.timeTrackings.reduce((total, tracking) => {
        return total + tracking.duration;
      }, 0);
    },
    timeTrackings() {
      return this.getTimeTrackings;
    },
    isProjectContext() {
      return this.project;
    },
    timeTrackingStates() {
      if (!this.getEnumValues('TimeTrackingState')) {
        return [];
      }
      return this.getEnumValues('TimeTrackingState').map((timeTrackingState) => {
        return { value: timeTrackingState, label: this.$t(timeTrackingState) };
      });
    },
    pageOffset() {
      return this.tableState.pagination.page * this.tableState.pagination.rowsPerPage;
    },
    filterName() {
      return 'timeTrackings-table-filters';
    },
    customFilterName() {
      return 'timeTrackings-custom-table-filters';
    },
  },

  watch: {
    project: {
      handler(newVal) {
        if (newVal && newVal.number) {
          this.tableState.filters.projectNumber = {
            value: String(newVal.number),
            matchMode: FilterMatchMode.EQUALS,
          };
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions(['fetchEnumValues', 'fetchEmployees', 'fetchTimeTracking', 'fetchUsers']),
    ...mapActions('auth', ['refreshUser']),

    async loadTimeTrackingData() {
      try {
        await this.fetchTimeTracking({
          page: this.pageOffset,
          pageSize: this.tableState.pagination.rowsPerPage,
          sortField: this.tableState.sortField,
          sortOrder: this.tableState.sortOrder,
          filters: {
            projectNumber: this.tableState.filters.projectNumber,
            timeTrackingCustomerSearch: this.tableState.customFilters.timeTrackingCustomerSearch,
          },
        });
      } catch (error) {
        console.error('Fehler beim Laden der Zeitverfolgungsdaten:', error);
      }
    },
    formatDateWithDots(datetime) {
      const dateObj = new Date(datetime);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}.${month}.${year}`;
    },

    setupFilters() {
      const currentProjectNumber = this.tableState.filters?.projectNumber?.value;

      if (!this.tableState.filters) {
        this.tableState.filters = { ...this.defaultFilters };
      }
      if (currentProjectNumber || this.project?.number) {
        this.tableState.filters.projectNumber = {
          value: String(currentProjectNumber || this.project.number), // Convert to string
          matchMode: FilterMatchMode.EQUALS,
        };
      }

      if (!this.tableState.customFilters) {
        this.tableState.customFilters = { ...this.defaultCustomFilters };
      }

      this.tableState.pagination.page = 0;
      this.tableState.pagination.rowsPerPage = 10;
      this.tableState.sortField = this.tableState.sortField || 'startTime';
      this.tableState.sortOrder = this.tableState.sortOrder || -1;
    },

    async onFilter(event) {
      const filters = {
        projectNumber: this.tableState.filters.projectNumber?.value || null,
      };

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null) {
          delete filters[key];
        }
      });

      await this.loadTimeTrackingData(filters);
    },
    /**
     * Funktion zum Filtern des Zeitverfolgungsstatus.
     * @param {Array} value - Die ausgewählten Statuswerte, nach denen gefiltert werden soll.
     */
    async onTimeTrackingStateFilter(value) {
      this.tableState.customFilters.timeTrackingStates = this.timeTrackingStates.filter(
        (timeTrackingState) => value.includes(timeTrackingState)
      );

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadTimeTrackingData();
    },
    async onCustomerFilter(value) {
      this.tableState.customFilters.timeTrackingCustomerSearch.value = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadTimeTrackingData();
    },

    async onUserFilter(value) {
      this.tableState.customFilters.timeTrackingUserSearch.value = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadTimeTrackingData();
    },

    async onStateRestore(event) {
      const customFiltersFromStorage = JSON.parse(
        localStorage.getItem(this.tableState.customFilterName)
      );
      this.tableState.customFilters = customFiltersFromStorage
        ? customFiltersFromStorage
        : this.defaultCustomFilters;

      this.setupFilters();
    },

    rowClass(data) {
      return data.number === '_new' ? 'new' : null;
    },

    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
    },

    async onPage(event) {
      this.tableState.pagination.page = event.page;
      this.tableState.pagination.rowsPerPage = event.rows;
      await this.loadTimeTrackingData();
    },

    async onSort(event) {
      this.tableState.sortField = event.sortField;
      this.tableState.sortOrder = event.sortOrder;
      await this.loadTimeTrackingData();
    },

    onCalendarChange(e, data) {
      this.preventHideDialog = true;
      console.log('onCalendarChange():', e, data);
    },

    resetPreventHide() {
      this.$nextTick(() => {
        this.preventHideDialog = false;
      });
    },

    getTimeTrackingStateColor,

    showNewTimeTrackingDialog() {
      const now = new Date();
      const startTime = this.initStartTime(now);
      const stopTime = this.initStopTime(startTime);

      this.editedTimeTracking = {
        number: '_new',
        startTime: startTime,
        stopTime: stopTime,
        timeTrackingState: 'STOPPED',
        user: null,
      };
      this.isNewTimeTracking = true;
      this.displayEditDialog = true;
    },

    initStartTime(date) {
      return this.$dayjs(date)
        .hour(8)
        .minute(0)
        .second(0)
        .toDate();
    },

    initStopTime(date) {
      return this.$dayjs(this.initStartTime(date))
        .add(8, 'hours')
        .toDate();
    },

    showEditDialog(timeTracking) {
      // Create a copy of the timeTracking object to avoid direct mutation
      this.editedTimeTracking = {
        ...timeTracking,
        startTime: new Date(timeTracking.startTime),
        stopTime:
          timeTracking.timeTrackingState === 'STARTED'
            ? null
            : timeTracking.stopTime
            ? new Date(timeTracking.stopTime)
            : this.$dayjs(timeTracking.startTime)
                .add(8, 'hours')
                .toDate(),
      };
      // Cache the original time
      this.originalStartTime = this.$dayjs(timeTracking.startTime);
      this.isNewTimeTracking = false;
      this.displayEditDialog = true;
    },

    onCancelEdit() {
      this.displayEditDialog = false;
      this.editedTimeTracking = null;
      this.originalStartTime = null;
      this.isNewTimeTracking = false;
    },

    getDurationInMinutes(startTime, stopTime) {
      return this.$dayjs(stopTime).diff(this.$dayjs(startTime), 'minute');
    },

    onStartDateSelect(date) {
      console.log('onStartDateSelect():', date);
      this.editedTimeTracking.startTime = this.initStartTime(date);
    },

    onStopDateSelect(date) {
      this.editedTimeTracking.stopTime = this.initStopTime(date);
    },

    onStartTimeSelect(time) {
      // console.log('onStartTimeSelect():', time);
    },

    onStopTimeSelect(time) {
      // console.log('onStopTimeSelect():', time);
    },

    /**
     * Remove all filters
     * @param {*} event
     */
    async onRemoveFilters(event) {
      this.tableState.sortOrder = this.defaultSortOrder;
      this.tableState.sortField = this.defaultSortField;
      this.tableState.filters = this.defaultFilters;
      this.tableState.customFilters = this.defaultCustomFilters;
      this.tableState.pagination.page = 0;
      this.tableState.pagination.rowsPerPage = 10;
      this.tableState.sortField = 'startTime';
      this.tableState.sortOrder = -1;
      localStorage.removeItem(this.filterName);
      localStorage.removeItem(this.customFilterName);
      await this.loadTimeTrackingData();
    },

    onFilterUsers(query) {
      this.filteredUsers = [];
      this.getUsers.map((user) => {
        if (
          (user.firstname && user.firstname.toLowerCase().indexOf(query) >= 0) ||
          (user.lastname && user.lastname.toLowerCase().indexOf(query) >= 0)
        ) {
          this.filteredUsers.push(user);
        }
      });
    },
  },
  async created() {
    this.setupFilters();
    if (this.project) {
      this.tableState.filters.projectNumber = {
        value: String(this.project.number),
        matchMode: FilterMatchMode.EQUALS,
      };
    }
  },

  async mounted() {
    await this.fetchEnumValues('TimeTrackingState');
    await this.fetchUsers();
    this.filteredEmployees = this.getEmployees;
    this.filteredUsers = this.getUsers;

    await this.loadTimeTrackingData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-menu-button {
  display: none;
}
::v-deep .p-column-filter-clear-button {
  display: none;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.state-badge {
  margin-left: 1rem;
}

.user-info {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.date-info {
  color: #666;
  font-size: 0.95rem;
}

.time-inputs {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.user-input {
  width: 100%;
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .multiselect-users {
    width: 100%;
  }
}

.time-row {
  display: flex;
  gap: 1rem;
  width: 100%;
}

.time-input {
  flex: 1;
}

.time-input label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.time-input-with-calendar {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .p-button {
    padding: 0.5rem;
    min-width: 2.5rem;
    height: 2.5rem;
  }
}

.multiselect-users {
  width: 100%;
}

::v-deep .p-datepicker-title {
  display: flex;
}

::v-deep .p-calendar .p-inputtext {
  width: 100%;
}

::v-deep .p-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.date-calendar {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}
</style>
